import { useContext, useMemo } from 'react'

// types
import { type Conditions, type Effects, type StillMapEventBlueprint, type SyncMapEventInstance, type AsyncMapEventInstance } from 'src/types'

// CTX
import { NavigationDataContext, NavigationDispatchContext } from 'src/store/navigation/Store'
import { SaveDataContext, SaveDispatchContext } from 'src/store/save/Store'
import { MapDataContext, MapDispatchContext } from 'src/store/map/Store'
import { InventoryDataContext, InventoryDispatchContext } from 'src/store/inventory/Store'
import { LogDataContext, LogDispatchContext } from 'src/store/logs/Store'
import { StatsDataContext, StatsDispatchContext } from 'src/store/stats/Store'
import { LocationDataContext } from 'src/store/locations/Store'
import { ItemDataContext } from 'src/store/items/Store'
import { FightDataContext } from 'src/store/fights/Store'
import { NPCDataContext } from 'src/store/npcs/Store'
import { EventDataContext } from 'src/store/events/Store'

// actions
import { newGame, setVariable } from 'src/store/save/actions'
import { setSwitch, setSwitches } from 'src/store/navigation/actions'
import { resetMap, travel } from 'src/store/map/actions'
import { removeAllEquipment, resetQuantity } from 'src/store/inventory/actions'
import { resetPlayerStats, setEquippedStats } from 'src/store/stats/actions'


const useSharedStore = () => {
  // static data
  const { locations } = useContext(LocationDataContext)
  const { items } = useContext(ItemDataContext)
  const { fights } = useContext(FightDataContext)
  const { NPCs } = useContext(NPCDataContext)
  const { events } = useContext(EventDataContext)

  // stateful data
  const { navigation } = useContext(NavigationDataContext)
  const { dispatchNavigation } = useContext(NavigationDispatchContext)

  const { save } = useContext(SaveDataContext)
  const { dispatchSave } = useContext(SaveDispatchContext)

  const { map } = useContext(MapDataContext)
  const { dispatchMap } = useContext(MapDispatchContext)

  const { inventory } = useContext(InventoryDataContext)
  const { dispatchInventory } = useContext(InventoryDispatchContext)

  const { logs } = useContext(LogDataContext) 
  const { dispatchLogs } = useContext(LogDispatchContext)

  const { stats } = useContext(StatsDataContext)
  const { dispatchStats } = useContext(StatsDispatchContext)

  const memoisedCombinedState = useMemo(() => ({ locations, items, fights, NPCs, events, navigation, save, map, inventory, logs, stats}), [locations, items, fights, NPCs, navigation, save, map, inventory, logs, stats])

  // Define your action here
  const processEffects = (effects: Effects) => {
    if (effects.variables) {
      Object.keys(effects.variables).forEach(variableID => {
        if (variableID === 'location') {
          // @ts-expect-error
          const string = effects.variables[variableID]
          dispatchMap({
            type: 'SET_VARIABLE',
            variableID: 'location_id',
            string
          })
        } else {
          // @ts-expect-error
          const string = effects.variables[variableID]
          dispatchSave({
            type: 'SET_VARIABLE',
            variableID,
            string
          })
        }
      })
    }

    if (effects.switches) {
      Object.keys(effects.switches).forEach(switchID => {
        // MapContext specific
        if (switchID === 'day_time') {
          // @ts-expect-error
          const boolean = effects.switches[switchID]
          dispatchMap({
            type: 'SET_SWITCH',
            switchID,
            boolean
          })
        } else {
          // @ts-expect-error
          const boolean = effects.switches[switchID]
          dispatchSave({
            type: 'SET_SWITCH',
            switchID,
            boolean
          })
        }
      })
    }
  
    if (effects.counters) {
      Object.keys(effects.counters).forEach(counterID => {
        if (counterID === 'player_x' || counterID === 'player_y') {
          // @ts-expect-error
          const counterEffect = effects.counters[counterID]
          dispatchMap({
            type: 'SET_COUNTER',
            counterID,
            counterEffect
          })
        } else {
          // @ts-expect-error
          const counterEffect = effects.counters[counterID]
          dispatchSave({
            type: 'SET_COUNTER',
            counterID,
            counterEffect
          })
        }
      })
    }
  
    // should check if bag limit, otherwise send it to stash
    if (effects.items) {
      Object.entries(effects.items).forEach(([itemID, itemValue]) => {
        dispatchInventory({
          type: 'SET_QUANTITY',
          itemID,
          storage: 'bag',
          quantity: itemValue
        })
      })
    }
  
    // should check if bag limit, otherwise send it to stash
    if (effects.fight) {
      setVariable(dispatchSave, 'fight_id', effects.fight)
      setSwitch(dispatchNavigation, 'combat_open', true)
    }
  }

  const checkConditions = (conditions?: Conditions) => {
    if (conditions === undefined) { return true }

    if (conditions) {
      const conditionKeys = Object.keys(conditions)
      // eslint-disable-next-line no-restricted-syntax
      for (const conditionKey of conditionKeys) {
        if (conditionKey === 'switches') {
          const { switches } = conditions
          if (switches) {
            const switchKeys = Object.keys(switches)
            // eslint-disable-next-line no-restricted-syntax
            for (const switchKey of switchKeys) {
              // comparison logic
              const required = switches[switchKey]
              const current = save.switches[switchKey] || false
              if (required !== current) {
                return false
              }
            }
          }
        }

        if (conditionKey === 'counters') {
          const { counters } = conditions
          if (counters) {
            const counterKeys = Object.keys(counters)
            // eslint-disable-next-line no-restricted-syntax
            for (const counterKey of counterKeys) {
              // comparison logic
              const counter = counters[counterKey]

              const operator = Object.keys(counter)[0]
              const current = save.counters[counterKey] || 0
              // @ts-expect-error
              const valueOrVariableKey: string | number = counter[operator]

              const required = typeof valueOrVariableKey === 'number'
                ? valueOrVariableKey
                : typeof valueOrVariableKey === 'string'
                  ? Number.isNaN(valueOrVariableKey)
                    ? save.counters[valueOrVariableKey]
                    : Number(valueOrVariableKey)
                  : 0

              if (operator === '>' && !(current > required)) {
                return false
              }

              if (operator === '<' && !(current < required)) {
                return false
              }

              if (operator === '=' && !(current === required)) {
                return false
              }
            }
          }
        }

        if (conditionKey === 'items' && conditions.items) {
          const { items } = conditions
        
          for (const [itemID, itemConditions] of Object.entries(items)) {
            const baggedItemCount = inventory.bag[itemID] || 0
            const equippedList = Object.entries(inventory.equipped).map(([, itemID]) => itemID)
            const equippedItemCount = equippedList.includes(itemID) ? 1 : 0
            const ownedItemCount = baggedItemCount + equippedItemCount
        
            for (const [operator, preValue] of Object.entries(itemConditions)) {
              if (operator === '>' && !(ownedItemCount > Number(preValue))) {
                return false
              }
        
              if (operator === '<' && !(ownedItemCount < Number(preValue))) {
                return false
              }
        
              if (operator === '=' && !(ownedItemCount === Number(preValue))) {
                return false
              }
        
              if (operator === 'isEquipped' && preValue && !(equippedList.includes(itemID))) {
                return false
              }
            }
          }
        }
      }
    }
    return true
  }

  const processEvent = (mapEvent: StillMapEventBlueprint | SyncMapEventInstance | AsyncMapEventInstance) => {
    if (mapEvent.type === 'fight') {
      setVariable(dispatchSave, 'fight_id', mapEvent.id)
      setSwitch(dispatchNavigation, 'combat_open', true)
    }
  
    if (mapEvent.type === 'event') {
      setVariable(dispatchSave, 'event_id', mapEvent.id)
      setSwitch(dispatchNavigation, 'event_open', true)
    }
  
    if (mapEvent.type === 'stash') {
      setSwitch(dispatchNavigation, 'stash_open', true)
    }
    if (mapEvent.type === 'travel') {
      setSwitch(dispatchMap, 'movement_blocked', true)
      travel(dispatchMap, mapEvent.player_y, mapEvent.player_x, mapEvent.destination || mapEvent.id)
    }
  }

  // Memoize your selected data so that it only updates when necessary
  const combinedState = useMemo(() => memoisedCombinedState, [memoisedCombinedState])
  return { combinedState, processEffects, checkConditions, processEvent}
}

export default useSharedStore
